import React, { useState } from 'react'
import { CSVLink } from 'react-csv'
import { LabelKeyObject } from 'react-csv/lib/core'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Grid,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  SxProps,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import NetworkAPI from 'api/faber/NetworkAPI'
import DownloadIcon from 'assets/icons/circles-design/download_icon.png'
import PlusIcon from 'assets/icons/circles-design/plus.png'
import PageLayout from 'common/Layout/Page/PageLayout'
import { ColorBoxContent } from 'common/ui/colorBox'
import OrbitSession from 'hooks/useCookies'

export interface IWhitelistRow {
  Country: string
  'IMSI Profile ID': string
  Operator: string
  'Operator ID': string
  Voice: string
  SMS: string
  Data: string
  MCC: string
  MNC: string
  'LTE Support': string
}

const TEMPLATE_HEADERS: Array<LabelKeyObject> = [
  { label: 'Country', key: 'country' },
  { label: 'IMSI Profile ID', key: 'imsi' },
  { label: 'Operator', key: 'operator' },
  { label: 'Operator ID', key: 'operatorId' },
  { label: 'MCC', key: 'mcc' },
  { label: 'MNC', key: 'mnc' },
  { label: 'Voice', key: 'voice' },
  { label: 'SMS', key: 'sms' },
  { label: 'Data', key: 'data' },
  { label: 'LTE Support', key: 'lte' }
]

const TEMPLATE_DATA = [
  {
    country: 'India',
    imsi: 2,
    operator: 'Jio',
    operatorId: 848,
    mmc: 405,
    mnc: 840,
    voice: 'PILOT',
    sms: 'BLOCKED',
    data: 'ALLOWED',
    lte: 'TRUE'
  }
]

const stepIconSx: SxProps = {
  '.MuiStepIcon-root': {
    fill: 'black',
    fontWeight: 'bold'
  },
  '.Mui-active': {
    fill: 'blue',
    fontWeight: 'bold'
  }
}

const DownloadTemplate = () => {
  return (
    <div>
      <CSVLink
        data={TEMPLATE_DATA}
        filename='whitelist-upload.csv'
        headers={TEMPLATE_HEADERS}
        style={{ textDecoration: 'flex' }}
      >
        <Box
          display='flex'
          alignItems='center'
          sx={{ paddingTop: 3, paddingBottom: 3 }}
        >
          <Typography
            sx={{
              color: '#0000FF',
              fontSize: '18px',
              fontWeight: 'bold'
            }}
          >
            Download Template
          </Typography>
          <img
            src={DownloadIcon}
            alt='download-template-logo'
            width='20px'
            height='20px'
          />
        </Box>
      </CSVLink>
    </div>
  )
}

export default function UploadWhitelist() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [uploadedBatch, setUploadedBatch] = useState<any>(null)
  const [rows, setRows] = useState<IWhitelistRow[]>([])
  const [activeStep, setActiveStep] = useState(0)
  const [, setError] = useState<boolean>(false)
  const [whitelistName, setWhitelistName] = useState<string>('')
  const [whitelistId, setWhitelistId] = useState<string>('')

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleNextStep = () => setActiveStep((prev) => prev + 1)
  const handlePrevStep = () =>
    setActiveStep((prev) => {
      if (prev === 0) {
        navigate('..')
        return prev
      } else {
        return prev - 1
      }
    })

  const faberToken = OrbitSession.get('faberToken')

  const { mutate: uploadTelnaCSV } = useMutation(
    NetworkAPI.upsertNetworkDetails,
    {
      onError: () => {
        setError(true)
      },
      onSuccess: () => {
        toast.success('Added successfully')
        queryClient.invalidateQueries({ queryKey: ['getNetworkDetailList'] })
      }
    }
  )

  const parseCsv = (str): IWhitelistRow[] | undefined => {
    const rowArray = str.split('\n')

    const strippedHeader = rowArray[0].replaceAll('"', '').trim().split(',')
    const headerIndMap = new Map()

    strippedHeader.forEach((headerElem: string, ind: number) => {
      headerIndMap.set(headerElem, ind)
    })

    for (let index = 0; index < TEMPLATE_HEADERS.length; index++) {
      const isExists = headerIndMap.get(TEMPLATE_HEADERS[index].label)

      if (isExists == undefined) {
        toast.error('Invalid header')
        return
      }
    }

    const parsedArray: IWhitelistRow[] = []

    const headerStrippedArray = rowArray.slice(1)
    headerStrippedArray.forEach((rowStr: string) => {
      const cleanedRowArr = rowStr.replaceAll('"', '').split(',')

      const parsedRowObj: IWhitelistRow = {
        Country: cleanedRowArr[headerIndMap.get('Country')]?.trim(),
        'IMSI Profile ID':
          cleanedRowArr[headerIndMap.get('IMSI Profile ID')]?.trim(),
        Operator: cleanedRowArr[headerIndMap.get('Operator')]?.trim(),
        'Operator ID': cleanedRowArr[headerIndMap.get('Operator ID')]?.trim(),
        Voice: cleanedRowArr[headerIndMap.get('Voice')]?.trim(),
        SMS: cleanedRowArr[headerIndMap.get('SMS')]?.trim(),
        Data: cleanedRowArr[headerIndMap.get('Data')]?.trim(),
        'LTE Support': cleanedRowArr[headerIndMap.get('LTE Support')]?.trim(),
        MCC: cleanedRowArr[headerIndMap.get('MCC')]?.trim(),
        MNC: cleanedRowArr[headerIndMap.get('MNC')]?.trim()
      }

      parsedArray.push(parsedRowObj)
    })

    return parsedArray
  }

  const handleCSVParse = () => {
    if (!uploadedBatch) {
      return
    }

    setError(false)
    const reader = new FileReader()

    reader.onload = async (e) => {
      if (!e.target) {
        return
      }

      const text = e.target.result

      const parsedText = parseCsv(text)

      if (parsedText) {
        setRows(parsedText)
      }
    }
    reader.readAsText(uploadedBatch)
  }

  const ParseFileContents = () => {
    return (
      <>
        <div id='batchUpdate__uploadFileArea'>
          {!uploadedBatch ? (
            <>
              <Typography sx={{ paddingTop: 3, paddingBottom: 3 }}>
                Upload File:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '50%',
                  border: '1px dashed #B8C4D3',
                  borderRadius: '8px'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    direction: 'column',
                    margin: '50px auto'
                  }}
                >
                  <label
                    htmlFor='uploadWhitelistFile'
                    style={{ cursor: 'pointer' }}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <img src={PlusIcon} alt='plus-icon' width='20px' />
                      <Typography
                        variant='h6'
                        style={{
                          color: '#0000FF',
                          marginLeft: 8
                        }}
                      >
                        Select File
                      </Typography>
                    </Box>
                  </label>
                  <input
                    id='uploadWhitelistFile'
                    type='file'
                    accept='.csv'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setUploadedBatch(e.target?.files?.[0])
                    }}
                    hidden
                  />
                </Box>
              </Box>
            </>
          ) : null}
        </div>
        {uploadedBatch && (
          <Box display='flex'>
            <Typography>Uploaded File:</Typography>
            <Typography variant='h6'>{uploadedBatch.name}</Typography>
          </Box>
        )}

        <Typography variant='subtitle1' style={{ color: '#B8C4D3' }}>
          csv, max 8mb
        </Typography>
      </>
    )
  }

  const handleBatchSubmit = async () => {
    if (!whitelistName.trim()) {
      toast.error('Whitelist name is required')
      return
    }

    if (!whitelistId.trim()) {
      toast.error('Whitelist Id is required')
      return
    }

    const headers = TEMPLATE_HEADERS.map((hdrObj) => {
      return hdrObj.label
    }).join(',')

    const csvData = rows
      .map((row) => {
        return `${row.Country},${row['IMSI Profile ID']},${row.Operator},${row['Operator ID']},${row.MCC},${row.MNC},${row.Voice},${row.SMS},${row.Data},${row['LTE Support']}`
      })
      .join('\n')

    const csvString = `${headers}\n${csvData}`

    const formData = new FormData()
    formData.append('file', new Blob([csvString], { type: 'text/csv' }))
    formData.append('whitelistName', whitelistName)
    formData.append('whitelistId', whitelistId)

    uploadTelnaCSV({ faberToken, payload: formData })
  }

  const steps = [
    {
      label: 'Download Template',
      description:
        'Download and follow this template to upload Whitelist to database.',
      component: <DownloadTemplate />,
      handleNextStep,
      handlePrevStep
    },
    {
      label: 'Parse File Contents',
      description:
        'Assign the Country, IMSI Profile ID, Operator, Operator ID,MCC,MNC Voice, SMS, Data, LTE and upload the file here.',
      component: <ParseFileContents />,
      handleNextStep: () => {
        handleCSVParse()
        handleNextStep()
      },
      handlePrevStep: () => {
        handlePrevStep()
        setUploadedBatch(null)
        setRows([])
      }
    },
    {
      label: 'Whitelist Details',
      component: (
        <>
          <TextField
            id='Whitelist name'
            label='Whitelist name'
            variant='outlined'
            value={whitelistName}
            onChange={(e) => {
              setWhitelistName(e.target.value)
            }}
          />
          <TextField
            id='Whitelist Id'
            label='Whitelist id'
            variant='outlined'
            sx={{ ml: '5px' }}
            value={whitelistId}
            onChange={(e) => {
              setWhitelistId(e.target.value)
            }}
          />
        </>
      ),
      handleNextStep: async () => {
        handleBatchSubmit()
        handleNextStep()
      }
    }
  ]

  return (
    <PageLayout header='Whitelist Upload'>
      <Grid container direction='column' spacing={2}>
        <Grid item>
          <ColorBoxContent variant='white'>
            <Stepper
              activeStep={activeStep}
              orientation='vertical'
              sx={stepIconSx}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === steps.length - 1 ? (
                        <Typography variant='caption'>Last step</Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    <Box> {step.component}</Box>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant='contained'
                          onClick={step.handleNextStep}
                          sx={{ mt: 1, mr: 1 }}
                          disabled={
                            index === steps.length - 1 &&
                            (!whitelistName.trim() || !whitelistId.trim())
                          }
                        >
                          {index === steps.length - 1 ? 'Submit' : 'Continue'}
                        </Button>
                        <Button
                          onClick={step?.handlePrevStep ?? handlePrevStep}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3 }}>
                <Typography>All steps completed!</Typography>
                <Button onClick={() => navigate('..')} sx={{ mt: 1, mr: 1 }}>
                  Back
                </Button>
              </Paper>
            )}
          </ColorBoxContent>
        </Grid>
      </Grid>
    </PageLayout>
  )
}
